import { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useLocation, useNavigate } from 'react-router-dom';
import Cropper from 'react-easy-crop';
import {
  Briefcase,
  Building2,
  CalendarDays,
  Camera,
  ChevronLeft,
  ChevronRight,
  Eye,
  EyeOff,
  HelpCircle,
  Info,
  ExternalLink,
  LogOut,
  Mail,
  MapPin,
  PencilLine,
  Phone,
  Settings,
  Shield,
  ShieldCheck,
  ShieldOff,
  Star,
  User,
} from 'lucide-react';
import AppLayout from '@components/AppLayout';
import { fileToDataUrl, getCroppedImageDataUrl } from '../../src/utils/imageCrop';
import { resolveBackendAssetUrl } from '../../src/utils/assetUrl';
import '../styles/account.css';
import '../styles/dashboard.css';
import '../styles/membership-form-wizard.css';
import { API_BASE_URL } from '../../src/config';

const PHOTO_CROP_FRAME_RATIO = 1;

const computeMinimumCropZoom = (width, height) => {
  if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) {
    return 1;
  }
  const aspectRatio = width / height;
  const zoomRequired = PHOTO_CROP_FRAME_RATIO * aspectRatio;
  return Number(Math.max(1, zoomRequired).toFixed(2));
};

const formatDate = (value, options) => {
  if (!value) return 'Not provided';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return date.toLocaleDateString('en-US', options);
};

const getInitials = (firstName = '', lastName = '') => {
  const initials = `${firstName.charAt(0)}${lastName.charAt(0)}`.trim().toUpperCase();
  return initials || 'LC';
};

function AccountModal({ title, tone, size, icon: Icon, onClose, children }) {
  return (
    <div className="account-modal">
      <div className="account-modal__backdrop" role="presentation" onClick={onClose} />
      <div className={`account-modal__panel account-modal__panel--${tone} account-modal__panel--${size}`}>
        <header>
          <div className="account-modal__icon">
            <Icon size={18} />
          </div>
          <h2>{title}</h2>
          <button type="button" onClick={onClose} aria-label="Close details">
            ×
          </button>
        </header>
        <div className="account-modal__content">{children}</div>
      </div>
    </div>
  );
}

AccountModal.propTypes = {
  title: PropTypes.string.isRequired,
  tone: PropTypes.oneOf(['blue', 'green', 'purple']).isRequired,
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
  icon: PropTypes.elementType.isRequired,
  onClose: PropTypes.func.isRequired,
  children: PropTypes.node.isRequired,
};

AccountModal.defaultProps = {
  size: 'md',
};

const getDisplayGender = (gender, otherGender) => {
  if (!gender) return 'Not provided';
  if (gender === 'Other' && otherGender) return otherGender;
  return gender;
};

export default function AccountPage({ user, onLogout }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [showPersonal, setShowPersonal] = useState(false);
  const [showContact, setShowContact] = useState(false);
  const [showEmployment, setShowEmployment] = useState(false);
  const [showFaq, setShowFaq] = useState(false);
  const [showAbout, setShowAbout] = useState(false);
  const [showSupport, setShowSupport] = useState(false);
  const [showRate, setShowRate] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [rating, setRating] = useState(0);
  const [feedback, setFeedback] = useState('');
  const [selectedAspects, setSelectedAspects] = useState([]);
  const [allowFollowUp, setAllowFollowUp] = useState(false);
  const [submittedFeedback, setSubmittedFeedback] = useState(false);
  const [preferences, setPreferences] = useState({
    emailAlerts: true,
    smsAlerts: false,
    monthlyDigest: true,
    betaFeatures: false,
  });
  const [passwordForm, setPasswordForm] = useState({ current: '', next: '', confirm: '' });
  const [passwordStatus, setPasswordStatus] = useState({ error: '', success: '' });
  const [passwordOtpCode, setPasswordOtpCode] = useState('');
  const [passwordVerificationToken, setPasswordVerificationToken] = useState('');
  const [passwordOtpLoading, setPasswordOtpLoading] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showEditPassword, setShowEditPassword] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [editOtherValue, setEditOtherValue] = useState('');
  const [editPassword, setEditPassword] = useState('');
  const [editStatus, setEditStatus] = useState({ error: '', success: '' });
  const [editLoading, setEditLoading] = useState(false);
  const [showAvatarEdit, setShowAvatarEdit] = useState(false);
  const [showVerificationPrompt, setShowVerificationPrompt] = useState(false);
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [avatarEditPassword, setAvatarEditPassword] = useState('');
  const [avatarEditLoading, setAvatarEditLoading] = useState(false);
  const [avatarEditStatus, setAvatarEditStatus] = useState({ error: '', success: '' });
  const [showAvatarEditPassword, setShowAvatarEditPassword] = useState(false);
  const [savedAvatarUrl, setSavedAvatarUrl] = useState(null);
  const [avatarCropSource, setAvatarCropSource] = useState(null);
  const [avatarCrop, setAvatarCrop] = useState({ x: 0, y: 0 });
  const [avatarZoom, setAvatarZoom] = useState(1);
  const [avatarMinZoom, setAvatarMinZoom] = useState(1);
  const [avatarCroppedAreaPixels, setAvatarCroppedAreaPixels] = useState(null);
  const [avatarCropError, setAvatarCropError] = useState('');
  const [isApplyingAvatarCrop, setIsApplyingAvatarCrop] = useState(false);
  const [isAvatarCropInteracting, setIsAvatarCropInteracting] = useState(false);
  const avatarCropperStageRef = useRef(null);
  const [avatarCropSize, setAvatarCropSize] = useState(undefined);
  const avatarMaxZoom = Math.max(3, Number((avatarMinZoom + 2).toFixed(2)));

  useEffect(() => {
    if (location.state?.openSupportModal) {
      setShowSupport(true);
      navigate('/account', { replace: true, state: {} });
    }
  }, [location.state?.openSupportModal, navigate]);
  const passwordRequirements = useMemo(
    () => [
      {
        label: 'At least 8 characters',
        test: (value) => value.length >= 8,
      },
      {
        label: 'One uppercase letter',
        test: (value) => /[A-Z]/.test(value),
      },
      {
        label: 'One lowercase letter',
        test: (value) => /[a-z]/.test(value),
      },
      {
        label: 'One number',
        test: (value) => /[0-9]/.test(value),
      },
      {
        label: 'One special character',
        test: (value) => /[^A-Za-z0-9]/.test(value),
      },
    ],
    [],
  );

  const togglePreference = (key) => {
    setPreferences((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const preferenceOptions = useMemo(
    () => [
      {
        key: 'emailAlerts',
        label: 'Email notifications',
        description: 'Receive account updates, approvals, and reminders through your registered email.',
      },
      {
        key: 'smsAlerts',
        label: 'SMS alerts',
        description: 'Get urgent announcements and time-sensitive notices via text message.',
      },
      {
        key: 'monthlyDigest',
        label: 'Monthly activity digest',
        description: 'Summary of dues, benefits, and new features sent at the start of every month.',
      },
      {
        key: 'betaFeatures',
        label: 'Preview beta features',
        description: 'Help test upcoming tools before public launch and share product feedback.',
      },
    ],
    [],
  );

  const ratingLabels = useMemo(
    () => ({
      1: 'Very dissatisfied',
      2: 'Needs improvement',
      3: 'Average experience',
      4: 'Good experience',
      5: 'Excellent experience',
    }),
    [],
  );

  const feedbackTags = useMemo(
    () => [
      'Ease of use',
      'Navigation',
      'Visual design',
      'Member services info',
      'Speed & performance',
      'Mobile responsiveness',
    ],
    [],
  );

  const ratingHint = rating ? ratingLabels[rating] : 'Tap a star to rate your experience';
  const trimmedFeedback = feedback.trim();
  const currentPasswordReady = passwordForm.current.trim().length > 0;
  const trimmedNewPassword = passwordForm.next.trim();
  const trimmedConfirmPassword = passwordForm.confirm.trim();
  const newPasswordIsValid = trimmedNewPassword.length > 0 && passwordRequirements.every(({ test }) => test(trimmedNewPassword));
  const confirmPasswordMatches =
    trimmedConfirmPassword.length > 0 && trimmedNewPassword === trimmedConfirmPassword;
  const isPasswordOtpStep = Boolean(passwordVerificationToken);
  const resolvedProfilePicture = useMemo(
    () => resolveBackendAssetUrl(savedAvatarUrl || user.profilePicture, { apiBaseUrl: API_BASE_URL }),
    [savedAvatarUrl, user.profilePicture],
  );
  const resolvedPasswordUpdatedAt = user.passwordUpdatedAt ?? user.lastPasswordChange ?? null;
  const sessionDeviceLabel = useMemo(() => {
    const sessionInfo = user.currentSession ?? {};
    const parts = [
      sessionInfo.browser || 'Unknown Browser',
      sessionInfo.deviceType || 'Web',
      sessionInfo.location,
    ].filter(Boolean);
    return parts.join(' • ');
  }, [user.currentSession]);

  const toggleAspect = (tag) => {
    setSelectedAspects((current) =>
      current.includes(tag) ? current.filter((item) => item !== tag) : [...current, tag],
    );
  };

  const resetRatingState = () => {
    setRating(0);
    setFeedback('');
    setSelectedAspects([]);
    setAllowFollowUp(false);
    setSubmittedFeedback(false);
  };

  const handleSubmitRating = () => {
    setFeedback((current) => current.trim());
    setSubmittedFeedback(true);
  };

  const updatePasswordForm = (field, value) => {
    setPasswordForm((prev) => ({ ...prev, [field]: value }));
    setPasswordStatus({ error: '', success: '' });
  };

  const resetPasswordState = () => {
    setPasswordForm({ current: '', next: '', confirm: '' });
    setPasswordStatus({ error: '', success: '' });
    setPasswordOtpCode('');
    setPasswordVerificationToken('');
    setPasswordOtpLoading(false);
    setShowNewPassword(false);
    setShowConfirmPassword(false);
    setShowCurrentPassword(false);
    setPasswordLoading(false);
  };

  const openFieldEditor = (field) => {
    if (!isVerified) {
      setShowVerificationPrompt(true);
      return;
    }
    const initialValue = field.value ?? '';
    const defaultOtherValue = field.otherValue ?? '';
    const optionValues = (field.options || []).map((option) =>
      typeof option === 'string' ? option : option.value,
    );
    const useOther = Boolean(field.options?.length) && initialValue && !optionValues.includes(initialValue);

    setEditingField(field);
    setEditValue(useOther ? 'other' : initialValue);
    setEditOtherValue(useOther ? initialValue : defaultOtherValue);
    setEditPassword('');
    setEditStatus({ error: '', success: '' });
    setEditLoading(false);
  };

  const closeFieldEditor = () => {
    setEditingField(null);
    setEditValue('');
    setEditOtherValue('');
    setEditPassword('');
    setEditStatus({ error: '', success: '' });
    setShowEditPassword(false);
    setEditLoading(false);
  };

  const closeAvatarEditor = () => {
    setShowAvatarEdit(false);
    setAvatarFile(null);
    setAvatarPreview(null);
    setAvatarEditPassword('');
    setAvatarEditStatus({ error: '', success: '' });
    setShowAvatarEditPassword(false);
    setAvatarEditLoading(false);
    setAvatarCropSource(null);
    setAvatarCrop({ x: 0, y: 0 });
    setAvatarZoom(1);
    setAvatarMinZoom(1);
    setAvatarCroppedAreaPixels(null);
    setAvatarCropError('');
    setIsApplyingAvatarCrop(false);
    setIsAvatarCropInteracting(false);
  };

  useEffect(() => {
    if (!avatarCropSource) {
      setAvatarCropSize(undefined);
      return undefined;
    }
    const stageElement = avatarCropperStageRef.current;
    if (!stageElement) return undefined;

    const syncCropSize = () => {
      const width = Math.floor(stageElement.clientWidth);
      const height = Math.floor(stageElement.clientHeight);
      if (width > 0 && height > 0) {
        const size = Math.min(width, height);
        setAvatarCropSize({ width: size, height: size });
      }
    };
    syncCropSize();

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', syncCropSize);
      return () => window.removeEventListener('resize', syncCropSize);
    }
    const resizeObserver = new ResizeObserver(syncCropSize);
    resizeObserver.observe(stageElement);
    return () => resizeObserver.disconnect();
  }, [avatarCropSource]);

  useEffect(() => {
    if (!isAvatarCropInteracting) return undefined;
    const stopInteraction = () => setIsAvatarCropInteracting(false);
    window.addEventListener('pointerup', stopInteraction);
    window.addEventListener('pointercancel', stopInteraction);
    return () => {
      window.removeEventListener('pointerup', stopInteraction);
      window.removeEventListener('pointercancel', stopInteraction);
    };
  }, [isAvatarCropInteracting]);

  const handleAvatarFileChange = async (event) => {
    const file = event.target.files?.[0] ?? null;
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      setAvatarEditStatus({ error: 'Image must be smaller than 5 MB.', success: '' });
      return;
    }
    try {
      const imageSource = await fileToDataUrl(file);
      setAvatarCropSource(imageSource);
      setAvatarCrop({ x: 0, y: 0 });
      setAvatarZoom(1);
      setAvatarMinZoom(1);
      setAvatarCroppedAreaPixels(null);
      setAvatarCropError('');
      setIsAvatarCropInteracting(false);
      setAvatarEditStatus({ error: '', success: '' });
    } catch (_) {
      setAvatarEditStatus({ error: 'Unable to load the selected photo. Please try another file.', success: '' });
    }
    event.target.value = '';
  };

  const handleAvatarCropComplete = (_, croppedAreaPixels) => {
    setAvatarCroppedAreaPixels(croppedAreaPixels);
  };

  const handleAvatarMediaLoaded = (mediaSize) => {
    const mediaWidth = mediaSize?.naturalWidth ?? mediaSize?.width;
    const mediaHeight = mediaSize?.naturalHeight ?? mediaSize?.height;
    const nextMinZoom = computeMinimumCropZoom(mediaWidth, mediaHeight);
    setAvatarMinZoom(nextMinZoom);
    setAvatarZoom((currentZoom) => Math.max(currentZoom, nextMinZoom));
  };

  const applyAvatarCrop = async () => {
    if (!avatarCropSource || !avatarCroppedAreaPixels) {
      setAvatarCropError('Adjust and confirm the crop before continuing.');
      return;
    }
    try {
      setIsApplyingAvatarCrop(true);
      const croppedDataUrl = await getCroppedImageDataUrl(
        avatarCropSource,
        avatarCroppedAreaPixels,
        256,
        'image/jpeg',
        0.72,
      );
      setAvatarPreview(croppedDataUrl);
      setAvatarFile(croppedDataUrl);
      setAvatarCropSource(null);
      setAvatarMinZoom(1);
      setAvatarCropError('');
      setIsAvatarCropInteracting(false);
    } catch (_) {
      setAvatarCropError('Unable to apply crop. Please try again.');
    } finally {
      setIsApplyingAvatarCrop(false);
    }
  };

  const handleAvatarUpdateSubmit = async (event) => {
    event.preventDefault();
    if (avatarEditLoading) return;

    if (!avatarFile) {
      setAvatarEditStatus({ error: 'Please select and crop an image to upload.', success: '' });
      return;
    }

    const password = avatarEditPassword.trim();
    if (!password) {
      setAvatarEditStatus({ error: 'Enter your account password to confirm this change.', success: '' });
      return;
    }

    setAvatarEditStatus({ error: '', success: '' });
    setAvatarEditLoading(true);

    try {
      const base64 = typeof avatarFile === 'string' ? avatarFile : await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(avatarFile);
      });

      const validateResponse = await fetch(`${API_BASE_URL}/api/users/${user.id}/validate-avatar-human`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ profilePicture: base64 }),
      });

      const validateData = await validateResponse.json().catch(() => ({}));
      if (!validateResponse.ok) {
        throw new Error(validateData.error || validateData.message || 'Unable to validate profile photo.');
      }

      if (!validateData.human_detected) {
        throw new Error('Please upload a photo that clearly shows a human face.');
      }

      const response = await fetch(`${API_BASE_URL}/api/users/${user.id}/update-avatar`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ profilePicture: base64, password }),
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(data.error || data.message || 'Unable to save change.');
      }

      if (data.profilePicture) {
        setSavedAvatarUrl(data.profilePicture);
        try {
          const stored = localStorage.getItem('alu_user');
          if (stored) {
            const parsed = JSON.parse(stored);
            localStorage.setItem('alu_user', JSON.stringify({ ...parsed, profilePicture: data.profilePicture }));
          }
        } catch (_) { /* non-critical */ }
      }
      setAvatarEditStatus({ error: '', success: data.message || 'Profile photo updated successfully.' });
    } catch (error) {
      setAvatarEditStatus({ error: error.message || 'Unable to update photo. Please try again.', success: '' });
    } finally {
      setAvatarEditLoading(false);
    }
  };

  const handleChangePasswordSubmit = async (event) => {
    event.preventDefault();
    if (passwordLoading || passwordOtpLoading) {
      return;
    }

    if (passwordVerificationToken) {
      await handlePasswordOtpConfirm();
      return;
    }

    const current = passwordForm.current.trim();
    const next = passwordForm.next.trim();
    const confirm = passwordForm.confirm.trim();

    if (!current || !next || !confirm) {
      setPasswordStatus({ error: 'Please fill in every field before updating your password.', success: '' });
      return;
    }

    const unmetRequirement = passwordRequirements.find(({ test }) => !test(next));
    if (unmetRequirement) {
      setPasswordStatus({
        error: `New password must include ${unmetRequirement.label.toLowerCase()}.`,
        success: '',
      });
      return;
    }

    if (next !== confirm) {
      setPasswordStatus({ error: 'New password and confirmation do not match.', success: '' });
      return;
    }

    if (current === next) {
      setPasswordStatus({ error: 'New password must be different from your current password.', success: '' });
      return;
    }

    if (!user?.id) {
      setPasswordStatus({ error: 'We could not confirm your account ID. Please sign out and back in before trying again.', success: '' });
      return;
    }

    setPasswordStatus({ error: '', success: '' });
    setPasswordLoading(true);

    try {
      const sessionToken = localStorage.getItem('alu_token');
      if (!sessionToken) {
        throw new Error('Your session expired. Please sign in again to change your password.');
      }

      const response = await fetch(`${API_BASE_URL}/api/auth/change-password/request`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${sessionToken}`,
        },
        body: JSON.stringify({
          currentPassword: current,
          newPassword: next,
        }),
      });
      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(data.message || 'Unable to update password.');
      }

      const verificationToken = data.verificationToken || '';
      if (!verificationToken) {
        throw new Error('Verification session was not created. Please try again.');
      }

      setPasswordVerificationToken(verificationToken);
      setPasswordOtpCode('');
      setPasswordStatus({ error: '', success: data.message || 'Enter OTP to finalize password change.' });
    } catch (error) {
      setPasswordStatus({
        error: error.message || 'Unable to update password. Please try again later.',
        success: '',
      });
    } finally {
      setPasswordLoading(false);
    }
  };

  const handlePasswordOtpConfirm = async () => {
    if (passwordOtpLoading) {
      return;
    }

    const otp = passwordOtpCode.trim();
    if (!/^\d{6}$/.test(otp)) {
      setPasswordStatus({ error: 'Enter the 6-digit OTP sent to your email.', success: '' });
      return;
    }

    if (!passwordVerificationToken) {
      setPasswordStatus({ error: 'Password change session expired. Please try again.', success: '' });
      return;
    }

    setPasswordOtpLoading(true);
    setPasswordStatus({ error: '', success: '' });

    try {
      const sessionToken = localStorage.getItem('alu_token');
      if (!sessionToken) {
        throw new Error('Your session expired. Please sign in again to change your password.');
      }

      const response = await fetch(`${API_BASE_URL}/api/auth/change-password/confirm`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${sessionToken}`,
        },
        body: JSON.stringify({
          verificationToken: passwordVerificationToken,
          otpCode: otp,
        }),
      });
      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(data.message || 'Unable to finalize password change.');
      }

      setPasswordForm({ current: '', next: '', confirm: '' });
      setPasswordVerificationToken('');
      setPasswordOtpCode('');
      setShowNewPassword(false);
      setShowConfirmPassword(false);
      setShowCurrentPassword(false);
      setPasswordStatus({ error: '', success: data.message || 'Password updated successfully.' });
    } catch (error) {
      setPasswordStatus({
        error: error.message || 'Unable to finalize password change. Please try again later.',
        success: '',
      });
    } finally {
      setPasswordOtpLoading(false);
    }
  };

  const handleFieldUpdateSubmit = async (event) => {
    event.preventDefault();
    if (editLoading || !editingField) {
      return;
    }

    if (!user?.id) {
      setEditStatus({ error: 'We could not confirm your account ID. Please sign out and back in.', success: '' });
      return;
    }

    const selectedValue = editValue.trim();
    const customValue = editOtherValue.trim();
    const finalValue = editingField.options?.length ? (selectedValue === 'other' ? customValue : selectedValue) : selectedValue;
    const password = editPassword.trim();

    if (!finalValue) {
      setEditStatus({ error: `Please enter a new ${editingField.label.toLowerCase()}.`, success: '' });
      return;
    }

    if (!password) {
      setEditStatus({ error: 'Enter your account password to verify this change.', success: '' });
      return;
    }

    setEditStatus({ error: '', success: '' });
    setEditLoading(true);

    try {
      const payload = {
        fieldKey: editingField.key,
        value: finalValue,
        password,
      };

      if (editingField.key === 'gender' && selectedValue === 'other') {
        payload.otherGender = customValue;
      }

      const response = await fetch(`${API_BASE_URL}/api/users/${user.id}/update-field`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        // Surface backend error details (if any) so members see what went wrong instead of a generic failure.
        throw new Error(data.error || data.message || 'Unable to save change.');
      }

      setEditStatus({ error: '', success: data.message || `${editingField.label} updated successfully.` });
    } catch (error) {
      setEditStatus({ error: error.message || 'Unable to save change. Please try again.', success: '' });
    } finally {
      setEditLoading(false);
    }
  };

  const faqEntries = [
    {
      question: 'How do I complete my membership profile after logging in?',
      answer:
        'Open the Membership Form from the dashboard or Quick Access panel and supply your employment, contact, and emergency contact details. Submit the form so the admin team can verify your application.',
    },
    {
      question: 'Where can I see the status of my registration or approval?',
      answer:
        'Visit the Dashboard or the Membership Form page. Pending items display status banners, and you will receive email notices once the admin approves your membership.',
    },
    {
      question: 'How are monthly dues tracked inside the portal?',
      answer:
        'The Dues Tracker shows every contribution, payroll deduction, or outstanding balance. You can download statements or contact the finance desk for reconciliation.',
    },
    {
      question: 'How do I request union assistance or benefits?',
      answer:
        'Navigate to the Benefits section and click “Request Assistance.” Choose the program, describe your request, and upload supporting documents. You will receive updates through the portal and email.',
    },
    {
      question: 'How can I get a digital or physical union ID?',
      answer:
        'After approval, go to the Digital ID page to access your QR-enabled card. For physical IDs, submit a request through admin or the ID Issuance queue, and you will receive collection instructions.',
    },
  ];

  const memberSinceYear = useMemo(() => {
    if (!user.membershipDate) return null;
    const raw = String(user.membershipDate);
    const date = new Date(raw.includes('T') ? raw : `${raw}T00:00:00`);
    return Number.isNaN(date.getTime()) ? null : date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }).toUpperCase();
  }, [user.membershipDate]);

  const profileSections = [
    {
      title: 'Personal Information',
      subtitle: 'Basic details and personal data',
      icon: User,
      tone: 'blue',
      action: () => setShowPersonal(true),
    },
    {
      title: 'Contact & Address',
      subtitle: 'Phone, email, and location details',
      icon: Mail,
      tone: 'green',
      action: () => setShowContact(true),
    },
    {
      title: 'Employment Details',
      subtitle: 'Company, role, and union profile',
      icon: Briefcase,
      tone: 'purple',
      action: () => setShowEmployment(true),
    },
  ];

  const menuItems = [
    {
      title: 'FAQs',
      subtitle: 'Frequently asked questions',
      icon: HelpCircle,
      action: () => setShowFaq(true),
    },
    {
      title: 'About LaborConnect',
      subtitle: 'Learn more about our union',
      icon: Info,
      action: () => setShowAbout(true),
    },
    {
      title: 'Contact Support',
      subtitle: 'Get help and assistance',
      icon: Phone,
      action: () => setShowSupport(true),
    },
    {
      title: 'Settings',
      subtitle: 'Notifications and preferences',
      icon: Settings,
      action: () => setShowSettings(true),
    },
  ];

  const aboutDetails = [
    {
      label: 'Who we are',
      value:
        'LaborConnect is the largest labor federation platform in the Philippines, advocating for workers’ rights and welfare since 1954.',
    },
    {
      label: 'What we do',
      value:
        'We provide collective bargaining support, legal and medical assistance, emergency relief, and training programs to empower members across Luzon.',
    },
    {
      label: 'Digital services',
      value:
        'LaborConnect centralizes membership registration, dues tracking, digital IDs, and benefits requests so members stay connected wherever they are.',
    },
    {
      label: 'Regional office',
      value: '262 15th Ave, Cubao, Quezon City, 1109 Metro Manila',
      mapUrl: 'https://www.google.com/maps?q=262+15th+Ave,+Cubao,+Quezon+City,+1109+Metro+Manila',
    },
  ];

  const supportContacts = [
    { icon: Phone, label: 'Hotline', value: '(02) 8123-4567' },
    { icon: Mail, label: 'Email', value: 'support@laborconnect.ph' },
    { icon: Building2, label: 'Office Hours', value: 'Mon – Fri • 8:00 AM – 5:00 PM' },
  ];

  const isVerified = user?.isApproved === 'active' || user?.isApproved === 'approved' || user?.isApproved === true || user?.isApproved === 1 || user?.isApproved === '1';

  const handleBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
      return;
    }
    navigate('/dashboard');
  };

  return (
    <AppLayout title="Account" user={user} onLogout={onLogout}>
      <div className="account-page">
        <section className="account-page__hero">
          <button type="button" className="account-page__back-button account-page__back-button--top" onClick={handleBack}>
            <ChevronLeft size={14} />
            <span>Back</span>
          </button>
          <div className="account-page__hero-content">
            <div style={{ position: 'relative', display: 'inline-block' }}>
              <button
                type="button"
                className="account-page__avatar"
                onClick={() => setShowPersonal(true)}
              >
                {resolvedProfilePicture ? (
                  <img src={resolvedProfilePicture} alt="Member" />
                ) : (
                  <span>{getInitials(user.firstName, user.lastName)}</span>
                )}
              </button>
              <button
                type="button"
                className="account-page__avatar-edit"
                aria-label="Edit profile photo"
                onClick={() => isVerified ? setShowAvatarEdit(true) : setShowVerificationPrompt(true)}
              >
                <PenIcon size={11} />
              </button>
            </div>
            <div className="account-page__hero-text">
              <button
                type="button"
                className="account-page__hero-name"
                onClick={() => setShowSettings(true)}
                aria-label="Open account settings"
              >
                {user.firstName || user.lastName ? `${user.firstName || ''} ${user.lastName || ''}`.trim() : 'Name Here'}
              </button>
              <button type="button" className="account-page__hero-link" onClick={() => setShowSettings(true)}>
                Account Settings
              </button>
            </div>
          </div>
        </section>

        <div className="account-page__layout">
          <section className="account-page__column">
            <div className="account-page__section-heading">
              <h2>Profile Information</h2>
            </div>
            <div className="account-page__panel-surface account-page__panel-surface--profile">
              <div className="account-menu">
                {profileSections.map((section) => {
                  const Icon = section.icon;
                  return (
                    <button
                      key={section.title}
                      type="button"
                      className="account-menu__item account-menu__item--profile"
                      onClick={section.action}
                    >
                      <div className="account-menu__icon">
                        <Icon size={18} />
                      </div>
                      <div className="account-menu__details">
                        <strong>{section.title}</strong>
                        <span>{section.subtitle}</span>
                      </div>
                      <ChevronRight size={18} />
                    </button>
                  );
                })}
              </div>
            </div>
          </section>

          <section className="account-page__column">
            <div className="account-page__section-heading">
              <h2>Account Settings</h2>
            </div>
            <div className="account-page__panel-surface account-page__panel-surface--features">
              <div className="account-menu">
                {menuItems.map((item) => {
                  const Icon = item.icon;
                  return (
                    <button
                      key={item.title}
                      type="button"
                      className={`account-menu__item${item.danger ? ' account-menu__item--danger' : ''}`}
                      onClick={item.action}
                    >
                      <div className="account-menu__icon">
                        <Icon size={18} />
                      </div>
                      <div className="account-menu__details">
                        <strong>{item.title}</strong>
                        <span>{item.subtitle}</span>
                      </div>
                      <ChevronRight size={18} />
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="account-page__panel-surface account-page__panel-surface--logout">
              <button
                type="button"
                className="account-menu__item account-menu__item--danger"
                onClick={() => setShowLogoutConfirm(true)}
              >
                <div className="account-menu__icon">
                  <LogOut size={18} />
                </div>
                <div className="account-menu__details">
                  <strong>Log out</strong>
                  <span>Sign out of your account</span>
                </div>
                <ChevronRight size={18} />
              </button>
            </div>
          </section>
        </div>
      </div>

      {showLogoutConfirm && (
        <AccountModal
          title="Confirm Logout"
          tone="purple"
          size="sm"
          icon={LogOut}
          onClose={() => setShowLogoutConfirm(false)}
        >
          <p style={{ margin: 0, color: '#475569', lineHeight: 1.6 }}>
            Are you sure you want to log out of your account?
          </p>
          <div className="account-modal__actions" style={{ marginTop: '8px' }}>
            <button
              type="button"
              className="button button--ghost"
              onClick={() => setShowLogoutConfirm(false)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="button"
              onClick={() => {
                setShowLogoutConfirm(false);
                onLogout();
              }}
            >
              Log out
            </button>
          </div>
        </AccountModal>
      )}

      {showPersonal && (
        <AccountModal
          title="Personal Details"
          tone="blue"
          icon={User}
          onClose={() => setShowPersonal(false)}
        >
          <div className="account-modal__profile">
            <div style={{ position: 'relative', display: 'inline-block' }}>
              <div className="account-modal__photo">
                {resolvedProfilePicture ? (
                  <img src={resolvedProfilePicture} alt="Member" />
                ) : (
                  <User size={32} />
                )}
              </div>
              <button
                type="button"
                aria-label="Edit profile photo"
                onClick={() => isVerified ? setShowAvatarEdit(true) : setShowVerificationPrompt(true)}
                style={{
                  position: 'absolute',
                  bottom: '-8px',
                  right: '-8px',
                  width: '26px',
                  height: '26px',
                  borderRadius: '50%',
                  border: '2px solid white',
                  background: '#123057',
                  color: '#d7e3ef',
                  display: 'grid',
                  placeItems: 'center',
                  cursor: 'pointer',
                  padding: 0,
                  zIndex: 1,
                }}
              >
                <PenIcon size={11} />
              </button>
            </div>
            <div>
              <strong>{user.firstName} {user.lastName}</strong>
              <span>{user.digitalId ?? 'LWU-000000'}</span>
            </div>
          </div>
          <div className="account-modal__list">
            <div className="account-field">
              <div className="account-field__meta">
                <span>Date of Birth</span>
                <strong>{formatDate(user.dateOfBirth, { month: 'long', day: 'numeric', year: 'numeric' })}</strong>
              </div>
              <div className="account-field__actions">
                <button
                  type="button"
                  className="account-field__button"
                  onClick={() =>
                    openFieldEditor({
                      key: 'dateOfBirth',
                      label: 'Date of Birth',
                      tone: 'blue',
                      icon: CalendarDays,
                      type: 'date',
                      value: user.dateOfBirth ?? '',
                    })
                  }
                >
                  <PenIcon size={14} />
                </button>
              </div>
            </div>
            <div className="account-field">
              <div className="account-field__meta">
                <span>Gender</span>
                <strong>{getDisplayGender(user.gender, user.otherGender)}</strong>
              </div>
              <div className="account-field__actions">
                <button
                  type="button"
                  className="account-field__button"
                  onClick={() =>
                    openFieldEditor({
                      key: 'gender',
                      label: 'Gender',
                      tone: 'blue',
                      icon: User,
                      options: [
                        { value: 'Male', label: 'Male' },
                        { value: 'Female', label: 'Female' },
                        { value: 'Prefer not to say', label: 'Prefer not to say' },
                        { value: 'other', label: 'Other' },
                      ],
                      value: user.gender === 'Other' ? 'other' : user.gender ?? '',
                      otherValue: user.otherGender ?? '',
                    })
                  }
                >
                  <PenIcon size={14} />
                </button>
              </div>
            </div>
            <div className="account-field">
              <div className="account-field__meta">
                <span>Religion</span>
                <strong>{user.religion ?? 'Not provided'}</strong>
              </div>
              <div className="account-field__actions">
                <button
                  type="button"
                  className="account-field__button"
                  onClick={() =>
                    openFieldEditor({
                      key: 'religion',
                      label: 'Religion',
                      tone: 'blue',
                      icon: User,
                      options: [
                        { value: 'Roman Catholic', label: 'Roman Catholic' },
                        { value: 'Islam', label: 'Islam' },
                        { value: 'Iglesia ni Cristo', label: 'Iglesia ni Cristo' },
                        { value: 'Seventh Day Adventist', label: 'Seventh Day Adventist' },
                        { value: 'Bible Baptist Church', label: 'Bible Baptist Church' },
                        { value: 'Jehovah\'s Witnesses', label: 'Jehovah\'s Witnesses' },
                        { value: 'Born Again Christian', label: 'Born Again Christian' },
                        { value: 'other', label: 'Other' },
                      ],
                      value: user.religion ?? '',
                    })
                  }
                >
                  <PenIcon size={14} />
                </button>
              </div>
            </div>
            <div className="account-field">
              <div className="account-field__meta">
                <span>Education</span>
                <strong>{user.education ?? 'Not provided'}</strong>
              </div>
              <div className="account-field__actions">
                <button
                  type="button"
                  className="account-field__button"
                  onClick={() =>
                    openFieldEditor({
                      key: 'education',
                      label: 'Education',
                      tone: 'blue',
                      icon: User,
                      options: [
                        { value: 'Elementary Graduate', label: 'Elementary Graduate' },
                        { value: 'High School Graduate', label: 'High School Graduate' },
                        { value: 'College Undergraduate', label: 'College Undergraduate' },
                        { value: 'College Graduate', label: 'College Graduate' },
                        { value: 'Vocational', label: 'Vocational' },
                        { value: "Master's Degree", label: "Master's Degree" },
                        { value: 'Doctorate Degree', label: 'Doctorate Degree' },
                        { value: 'other', label: 'Other' },
                      ],
                      value: user.education ?? '',
                    })
                  }
                >
                  <PenIcon size={14} />
                </button>
              </div>
            </div>
          </div>
        </AccountModal>
      )}

      {showContact && (
        <AccountModal
          title="Contact & Address"
          tone="green"
          icon={Mail}
          onClose={() => setShowContact(false)}
        >
          <div className="account-modal__list">
            {user.email && (
              <div className="account-field">
                <div className="account-field__meta">
                  <span>Email</span>
                  <strong>{user.email}</strong>
                </div>
                <div className="account-field__actions">
                  <button
                    type="button"
                    className="account-field__button"
                    onClick={() =>
                      openFieldEditor({
                        key: 'email',
                        label: 'Email',
                        tone: 'green',
                        icon: Mail,
                        type: 'email',
                        value: user.email ?? '',
                      })
                    }
                  >
                    <PenIcon size={14} />
                  </button>
                </div>
              </div>
            )}
            <div className="account-field">
              <div className="account-field__meta">
                <span>Phone</span>
                <strong>{user.phone ?? 'Not provided'}</strong>
              </div>
              <div className="account-field__actions">
                <button
                  type="button"
                  className="account-field__button"
                  onClick={() =>
                    openFieldEditor({
                      key: 'phone',
                      label: 'Phone',
                      tone: 'green',
                      icon: Phone,
                      type: 'tel',
                      value: user.phone ?? '',
                    })
                  }
                >
                  <PenIcon size={14} />
                </button>
              </div>
            </div>
            <div className="account-field">
              <div className="account-field__meta">
                <span>Address</span>
                <strong>{user.address ?? 'Not provided'}</strong>
              </div>
              <div className="account-field__actions">
                <button
                  type="button"
                  className="account-field__button"
                  onClick={() =>
                    openFieldEditor({
                      key: 'address',
                      label: 'Address',
                      tone: 'green',
                      icon: MapPin,
                      value: user.address ?? '',
                    })
                  }
                >
                  <PenIcon size={14} />
                </button>
              </div>
            </div>
          </div>
          {user.emergencyContact && (
            <div className="account-modal__emergency">
              <h3>Emergency Contact</h3>
              <div className="account-modal__list">
                <div className="account-field">
                  <div className="account-field__meta">
                    <span>Name</span>
                    <strong>{user.emergencyContact.name}</strong>
                  </div>
                  <div className="account-field__actions">
                    <button
                      type="button"
                      className="account-field__button"
                      onClick={() =>
                        openFieldEditor({
                          key: 'emergencyContactName',
                          label: 'Emergency Contact Name',
                          tone: 'green',
                          icon: User,
                          value: user.emergencyContact.name ?? '',
                        })
                      }
                    >
                      <PenIcon size={14} />
                    </button>
                  </div>
                </div>
                <div className="account-field">
                  <div className="account-field__meta">
                    <span>Relationship</span>
                    <strong>{user.emergencyContact.relationship}</strong>
                  </div>
                  <div className="account-field__actions">
                    <button
                      type="button"
                      className="account-field__button"
                      onClick={() =>
                        openFieldEditor({
                          key: 'emergencyContactRelationship',
                          label: 'Emergency Contact Relationship',
                          tone: 'green',
                          icon: User,
                          value: user.emergencyContact.relationship ?? '',
                        })
                      }
                    >
                      <PenIcon size={14} />
                    </button>
                  </div>
                </div>
                <div className="account-field">
                  <div className="account-field__meta">
                    <span>Phone</span>
                    <strong>{user.emergencyContact.phone}</strong>
                  </div>
                  <div className="account-field__actions">
                    <button
                      type="button"
                      className="account-field__button"
                      onClick={() =>
                        openFieldEditor({
                          key: 'emergencyContactPhone',
                          label: 'Emergency Contact Phone',
                          tone: 'green',
                          icon: Phone,
                          type: 'tel',
                          value: user.emergencyContact.phone ?? '',
                        })
                      }
                    >
                      <PenIcon size={14} />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </AccountModal>
      )}

      {showEmployment && (
        <AccountModal
          title="Employment Details"
          tone="purple"
          icon={Briefcase}
          onClose={() => setShowEmployment(false)}
        >
          <div className="account-modal__list">
            <div className="account-field">
              <div className="account-field__meta">
                <span>Company</span>
                <strong>{user.company ?? 'Not provided'}</strong>
              </div>
            </div>
            <div className="account-field">
              <div className="account-field__meta">
                <span>Position</span>
                <strong>{user.position ?? 'Not provided'}</strong>
              </div>
            </div>
            <div className="account-field">
              <div className="account-field__meta">
                <span>Union Name</span>
                <strong>{user.department ?? 'Not provided'}</strong>
              </div>
            </div>
            <div className="account-field">
              <div className="account-field__meta">
                <span>Years Employed</span>
                <strong>
                  {user.yearsEmployed != null
                    ? Number(user.yearsEmployed).toString()
                    : "Not provided"}
                </strong>
              </div>
            </div>
          </div>
          <div className="account-modal__list account-modal__list--accent">
            <div className="account-field">
              <div className="account-field__meta">
                <span>Union Position</span>
                <strong>{user.unionPosition ?? 'Member'}</strong>
              </div>
            </div>
            <div className="account-field">
              <div className="account-field__meta">
                <span>Member Since</span>
                <strong>{memberSinceYear ?? 'Pending Approval'}</strong>
              </div>
              <div className="account-field__actions">
                <button type="button" className="account-field__button" disabled>
                  <ShieldCheck size={14} />
                  Verified
                </button>
              </div>
            </div>
            <div className="account-field">
              <div className="account-field__meta">
                <span>Affiliation</span>
                <strong>{user.unionAffiliation ?? 'Not provided'}</strong>
              </div>
            </div>
          </div>
        </AccountModal>
      )}

      {showFaq && (
        <AccountModal
          title="Frequently Asked Questions"
          tone="blue"
          size="lg"
          icon={HelpCircle}
          onClose={() => setShowFaq(false)}
        >
          <div className="account-modal__list account-modal__list--faq">
            {faqEntries.map((entry) => (
              <div key={entry.question}>
                <span>{entry.question}</span>
                <strong>{entry.answer}</strong>
              </div>
            ))}
          </div>
        </AccountModal>
      )}

      {showAbout && (
        <AccountModal
          title="About LaborConnect"
          tone="purple"
          size="lg"
          icon={Info}
          onClose={() => setShowAbout(false)}
        >
          <div className="account-modal__list account-modal__list--about">
            {aboutDetails.map((entry) => (
              <div key={entry.label}>
                <span>{entry.label}</span>
                {entry.mapUrl ? (
                  <strong>
                    <a
                      className="account-modal__address-link"
                      href={entry.mapUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {entry.value}
                      <ExternalLink size={14} />
                    </a>
                  </strong>
                ) : (
                  <strong>{entry.value}</strong>
                )}
              </div>
            ))}
          </div>
          <div className="account-modal__list account-modal__list--accent account-modal__list--about">
            {supportContacts.map(({ icon: Icon, label, value }) => (
              <div key={label}>
                <span>{label}</span>
                <strong>
                  <Icon size={14} style={{ marginRight: 8 }} />
                  {value}
                </strong>
              </div>
            ))}
          </div>
          <p className="account-modal__footnote">
            LaborConnect is a proud founding affiliate of the Trade Union Congress of the Philippines, representing workers in
            19,000+ workplaces across Luzon.
          </p>
        </AccountModal>
      )}

      {showSupport && (
        <AccountModal
          title="Contact Member Support"
          tone="green"
          size="lg"
          icon={Phone}
          onClose={() => setShowSupport(false)}
        >
          <div className="account-modal__list account-modal__list--accent">
            {supportContacts.map(({ icon: Icon, label, value }) => (
              <div key={label}>
                <span>{label}</span>
                <strong>
                  <Icon size={14} style={{ marginRight: 8 }} />
                  {value}
                </strong>
              </div>
            ))}
          </div>

          <div className="account-modal__list">
            <div>
              <span>Emergency Assistance</span>
              <strong>Dial hotline and press 1 for urgent medical or legal support. Available 24/7.</strong>
            </div>
            <div>
              <span>Member Services Desk</span>
              <strong>Email documents to benefits@laborconnect.ph for faster processing of claims and requests.</strong>
            </div>
            <div>
              <span>Regional Offices</span>
              <strong>
                Luzon HQ, ALU-AFILUTE Building, 262 15th Ave., Cubao, Quezon City
              </strong>
            </div>
          </div>
        </AccountModal>
      )}

      {showSettings && (
        <AccountModal
          title="Account Settings"
          tone="blue"
          size="lg"
          icon={Settings}
          onClose={() => setShowSettings(false)}
        >
          <div className="account-settings">
            <div className="account-settings__group">
              {preferenceOptions.map(({ key, label, description }) => (
                <label key={key} className="account-toggle">
                  <input
                    type="checkbox"
                    checked={preferences[key]}
                    onChange={() => togglePreference(key)}
                    aria-label={label}
                  />
                  <span className="account-toggle__switch">
                    <span className="account-toggle__thumb" />
                  </span>
                  <div className="account-toggle__meta">
                    <strong>{label}</strong>
                    <p>{description}</p>
                  </div>
                </label>
              ))}
            </div>

            <div className="account-settings__cta">
              <div className="account-settings__cta-text">
                <strong>Keep your account secure</strong>
                <span>Refresh your password regularly to protect your membership information.</span>
              </div>
              <button
                type="button"
                className="button button--secondary"
                onClick={() => {
                  setShowSettings(false);
                  setShowChangePassword(true);
                }}
              >
                Change password
              </button>
            </div>
          </div>

          <div className="account-modal__list account-modal__list--accent">
            <div>
              <span>Last password update</span>
              <strong>{formatDate(resolvedPasswordUpdatedAt, { month: 'long', day: 'numeric', year: 'numeric' })}</strong>
            </div>
            <div>
              <span>Session devices</span>
              <strong>{sessionDeviceLabel || 'Session data unavailable'}</strong>
            </div>
          </div>
        </AccountModal>
      )}

      {showChangePassword && (
        <AccountModal
          title="Change Password"
          tone="purple"
          size="md"
          icon={Shield}
          onClose={() => {
            setShowChangePassword(false);
            resetPasswordState();
          }}
        >
          <form className="account-modal__form" onSubmit={handleChangePasswordSubmit}>
            <div className="account-modal__field">
              <label htmlFor="account-password-current">Current password</label>
              <div className="account-modal__input-group">
                <input
                  id="account-password-current"
                  type={showCurrentPassword ? 'text' : 'password'}
                  value={passwordForm.current}
                  onChange={(event) => updatePasswordForm('current', event.target.value)}
                  placeholder="Enter your current password"
                  autoComplete="current-password"
                  disabled={isPasswordOtpStep}
                />
                <button
                  type="button"
                  className="account-modal__input-toggle"
                  onClick={() => setShowCurrentPassword((prev) => !prev)}
                  aria-label={showCurrentPassword ? 'Hide current password' : 'Show current password'}
                  disabled={isPasswordOtpStep}
                >
                  {showCurrentPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            <div
              className={`account-modal__field${newPasswordIsValid ? ' account-modal__field--valid' : ''}`}
            >
              <label htmlFor="account-password-new">New password</label>
              <div className="account-modal__input-group">
                <input
                  id="account-password-new"
                  type={showNewPassword ? 'text' : 'password'}
                  value={passwordForm.next}
                  onChange={(event) => updatePasswordForm('next', event.target.value)}
                  placeholder="Choose a new password"
                  autoComplete="new-password"
                  disabled={isPasswordOtpStep}
                />
                <button
                  type="button"
                  className="account-modal__input-toggle"
                  onClick={() => setShowNewPassword((prev) => !prev)}
                  aria-label={showNewPassword ? 'Hide new password' : 'Show new password'}
                  disabled={isPasswordOtpStep}
                >
                  {showNewPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            <div className="account-password-reqs">
              <strong>Password Requirements:</strong>
              <ul>
                {passwordRequirements.map(({ label, test }) => {
                  const met = test(trimmedNewPassword);
                  return (
                    <li
                      key={label}
                      className={`account-password-req${met ? ' account-password-req--met' : ''}`}
                    >
                      <span className="account-password-req__icon" aria-hidden="true">
                        {met ? '✓' : '•'}
                      </span>
                      <span>{label}</span>
                    </li>
                  );
                })}
              </ul>
            </div>
            <div
              className={`account-modal__field${
                confirmPasswordMatches ? ' account-modal__field--valid' : ''
              }`}
            >
              <label htmlFor="account-password-confirm">Confirm new password</label>
              <div className="account-modal__input-group">
                <input
                  id="account-password-confirm"
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={passwordForm.confirm}
                  onChange={(event) => updatePasswordForm('confirm', event.target.value)}
                  placeholder="Re-enter the new password"
                  autoComplete="new-password"
                  disabled={isPasswordOtpStep}
                />
                <button
                  type="button"
                  className="account-modal__input-toggle"
                  onClick={() => setShowConfirmPassword((prev) => !prev)}
                  aria-label={showConfirmPassword ? 'Hide confirmation password' : 'Show confirmation password'}
                  disabled={isPasswordOtpStep}
                >
                  {showConfirmPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            {isPasswordOtpStep ? (
              <>
                <p className="account-modal__hint">
                  Enter the 6-digit OTP sent to your email to complete your password change.
                </p>
                <div className="account-modal__field">
                  <label htmlFor="account-password-otp-inline">Email OTP</label>
                  <div className="account-modal__input-group">
                    <input
                      id="account-password-otp-inline"
                      type="text"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      placeholder="000000"
                      maxLength={6}
                      value={passwordOtpCode}
                      onChange={(event) => {
                        const value = event.target.value;
                        if (/^\d{0,6}$/.test(value)) {
                          setPasswordOtpCode(value);
                          setPasswordStatus({ error: '', success: '' });
                        }
                      }}
                    />
                  </div>
                </div>
              </>
            ) : null}

            {passwordStatus.error ? (
              <p className="account-modal__status account-modal__status--error">{passwordStatus.error}</p>
            ) : null}
            {passwordStatus.success ? (
              <p className="account-modal__status account-modal__status--success">{passwordStatus.success}</p>
            ) : null}

            <div className="account-modal__actions">
              <button
                type="submit"
                className="button button--secondary"
                disabled={passwordLoading || passwordOtpLoading}
                aria-busy={passwordLoading || passwordOtpLoading}
              >
                {passwordLoading
                  ? 'Sending OTP...'
                  : passwordOtpLoading
                    ? 'Verifying...'
                    : isPasswordOtpStep
                      ? 'Verify & Update Password'
                      : 'Send OTP'}
              </button>
              <button
                type="button"
                className="button button--ghost"
                onClick={() => {
                  setShowChangePassword(false);
                  resetPasswordState();
                }}
              >
                Cancel
              </button>
            </div>
          </form>
        </AccountModal>
      )}

      {editingField && (
        <AccountModal
          title={`Update ${editingField.label}`}
          tone={editingField.tone || 'blue'}
          size="sm"
          icon={editingField.icon || User}
          onClose={closeFieldEditor}
        >
          <form className="account-modal__form account-modal__form--edit" onSubmit={handleFieldUpdateSubmit}>
            <div className="account-modal__field">
              <label htmlFor={`account-edit-${editingField.key}`}>
                New {editingField.label.toLowerCase()}
              </label>
              {editingField.options?.length ? (
                <>
                  <select
                    id={`account-edit-${editingField.key}`}
                    value={editValue}
                    onChange={(event) => {
                      setEditValue(event.target.value);
                      setEditStatus({ error: '', success: '' });
                      if (event.target.value !== 'other') {
                        setEditOtherValue('');
                      }
                    }}
                  >
                    <option value="">Select {editingField.label.toLowerCase()}</option>
                    {editingField.options.map((option) => {
                      const value = typeof option === 'string' ? option : option.value;
                      const label = typeof option === 'string' ? option : option.label;
                      return (
                        <option key={value} value={value}>
                          {label}
                        </option>
                      );
                    })}
                  </select>
                  {editValue === 'other' ? (
                    <input
                      type="text"
                      value={editOtherValue}
                      onChange={(event) => {
                        setEditOtherValue(event.target.value);
                        setEditStatus({ error: '', success: '' });
                      }}
                      placeholder={`Enter other ${editingField.label.toLowerCase()}`}
                    />
                  ) : null}
                </>
              ) : (
                <input
                  id={`account-edit-${editingField.key}`}
                  type={editingField.type || 'text'}
                  value={editValue}
                  onChange={(event) => {
                    setEditValue(event.target.value);
                    setEditStatus({ error: '', success: '' });
                  }}
                  placeholder={`Enter new ${editingField.label.toLowerCase()}`}
                />
              )}
            </div>
            <div className="account-modal__field">
              <label htmlFor="account-edit-password">Account password</label>
              <div className="account-modal__input-group">
                <input
                  id="account-edit-password"
                  type={showEditPassword ? 'text' : 'password'}
                  value={editPassword}
                  onChange={(event) => {
                    setEditPassword(event.target.value);
                    setEditStatus({ error: '', success: '' });
                  }}
                  placeholder="Enter your password to confirm"
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  className="account-modal__input-toggle"
                  onClick={() => setShowEditPassword((prev) => !prev)}
                  aria-label={showEditPassword ? 'Hide password' : 'Show password'}
                >
                  {showEditPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            {editStatus.error ? (
              <p className="account-modal__status account-modal__status--error">{editStatus.error}</p>
            ) : null}
            {editStatus.success ? (
              <p className="account-modal__status account-modal__status--success">{editStatus.success}</p>
            ) : null}

            <div className="account-modal__actions">
              <button type="submit" className="button button--secondary" disabled={editLoading} aria-busy={editLoading}>
                {editLoading ? 'Saving...' : 'Save change'}
              </button>
              <button type="button" className="button button--ghost" onClick={closeFieldEditor}>
                Cancel
              </button>
            </div>
          </form>
        </AccountModal>
      )}

      {showAvatarEdit && (
        <AccountModal
          title="Update Profile Photo"
          tone="blue"
          size="md"
          icon={Camera}
          onClose={closeAvatarEditor}
        >
          <form className="account-modal__form" onSubmit={handleAvatarUpdateSubmit}>
            {avatarCropSource ? (
              <div className="photo-crop-editor">
                <div
                  ref={avatarCropperStageRef}
                  className="photo-cropper-stage"
                  onPointerDown={() => setIsAvatarCropInteracting(true)}
                  onPointerCancel={() => setIsAvatarCropInteracting(false)}
                >
                  <Cropper
                    image={avatarCropSource}
                    crop={avatarCrop}
                    cropSize={avatarCropSize}
                    zoom={avatarZoom}
                    minZoom={avatarMinZoom}
                    aspect={1}
                    cropShape="round"
                    objectFit="cover"
                    restrictPosition
                    showGrid={false}
                    onCropChange={setAvatarCrop}
                    onZoomChange={setAvatarZoom}
                    onCropComplete={handleAvatarCropComplete}
                    onMediaLoaded={handleAvatarMediaLoaded}
                  />
                  <div
                    className={`photo-crop-grid-guide${isAvatarCropInteracting ? ' is-visible' : ''}`}
                    aria-hidden="true"
                  />
                </div>
                <div className="photo-crop-controls">
                  <label className="photo-crop-zoom-field">
                    <span>Zoom</span>
                    <input
                      type="range"
                      min={avatarMinZoom}
                      max={avatarMaxZoom}
                      step={0.01}
                      value={avatarZoom}
                      onChange={(e) => setAvatarZoom(Number(e.target.value))}
                    />
                  </label>
                  <div className="photo-crop-action-row">
                    <button type="button" className="button button--secondary" onClick={applyAvatarCrop} disabled={isApplyingAvatarCrop}>
                      {isApplyingAvatarCrop ? 'Applying...' : 'Apply Crop'}
                    </button>
                    <button
                      type="button"
                      className="button button--ghost"
                      onClick={() => {
                        setAvatarCropSource(null);
                        setIsAvatarCropInteracting(false);
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
                {avatarCropError ? (
                  <p className="account-modal__status account-modal__status--error">{avatarCropError}</p>
                ) : null}
              </div>
            ) : (
              <>
                <div style={{ display: 'flex', justifyContent: 'center', padding: '8px 0' }}>
                  <div style={{
                    width: '88px',
                    height: '88px',
                    borderRadius: '50%',
                    overflow: 'hidden',
                    background: 'linear-gradient(135deg, #2563eb, #7c3aed)',
                    display: 'grid',
                    placeItems: 'center',
                    flexShrink: 0,
                  }}>
                    {(avatarPreview || resolvedProfilePicture) ? (
                      <img
                        src={avatarPreview || resolvedProfilePicture}
                        alt="Preview"
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    ) : (
                      <User size={36} style={{ color: 'white' }} />
                    )}
                  </div>
                </div>

                <div className="account-modal__field">
                  <label htmlFor="account-avatar-file">{avatarPreview ? 'Choose different photo' : 'New profile photo'}</label>
                  <input
                    id="account-avatar-file"
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/gif"
                    onChange={handleAvatarFileChange}
                  />
                </div>

                <div className="account-modal__field">
                  <label htmlFor="account-avatar-password">Account password</label>
                  <div className="account-modal__input-group">
                    <input
                      id="account-avatar-password"
                      type={showAvatarEditPassword ? 'text' : 'password'}
                      value={avatarEditPassword}
                      onChange={(event) => {
                        setAvatarEditPassword(event.target.value);
                        setAvatarEditStatus({ error: '', success: '' });
                      }}
                      placeholder="Enter your password to confirm"
                      autoComplete="current-password"
                    />
                    <button
                      type="button"
                      className="account-modal__input-toggle"
                      onClick={() => setShowAvatarEditPassword((prev) => !prev)}
                      aria-label={showAvatarEditPassword ? 'Hide password' : 'Show password'}
                    >
                      {showAvatarEditPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>

                {avatarEditStatus.error ? (
                  <p className="account-modal__status account-modal__status--error">{avatarEditStatus.error}</p>
                ) : null}
                {avatarEditStatus.success ? (
                  <p className="account-modal__status account-modal__status--success">{avatarEditStatus.success}</p>
                ) : null}

                <div className="account-modal__actions">
                  <button type="submit" className="button button--secondary" disabled={avatarEditLoading} aria-busy={avatarEditLoading}>
                    {avatarEditLoading ? 'Saving...' : 'Save photo'}
                  </button>
                  <button type="button" className="button button--ghost" onClick={closeAvatarEditor}>
                    Cancel
                  </button>
                </div>
              </>
            )}
          </form>
        </AccountModal>
      )}

      {showRate && (
        <AccountModal
          title="Rate our Website"
          tone="blue"
          size="lg"
          icon={Star}
          onClose={() => {
            setShowRate(false);
            resetRatingState();
          }}
        >
          <div className="account-rating__hero">
            <div className="account-rating__icon">⭐</div>
            <div className="account-rating__summary">
              <h3>Overall experience</h3>
              <p>Tell us how well the LaborConnect member portal supports your needs today.</p>
              <div
                className="account-rating__stars"
                role="radiogroup"
                aria-label="Overall website experience rating"
              >
                {[1, 2, 3, 4, 5].map((value) => (
                  <button
                    type="button"
                    key={value}
                    className={`account-rating__star${rating >= value ? ' account-rating__star--active' : ''}`}
                    onClick={() => setRating(value)}
                    aria-label={`Rate ${value} star${value > 1 ? 's' : ''}`}
                    aria-pressed={rating === value}
                  >
                    ★
                  </button>
                ))}
              </div>
              <p className="account-rating__hint">{ratingHint}</p>
            </div>
          </div>

          <div className="account-rating__section">
            <header>
              <h4>Focus areas</h4>
              <p>Select the parts of the experience you want us to improve or keep strong.</p>
            </header>
            <div
              className="account-rating__chips"
              role="group"
              aria-label="Focus areas for feedback"
            >
              {feedbackTags.map((tag) => (
                <button
                  type="button"
                  key={tag}
                  className={`account-rating__chip${selectedAspects.includes(tag) ? ' account-rating__chip--active' : ''}`}
                  onClick={() => toggleAspect(tag)}
                >
                  <span className="account-rating__chip-dot" aria-hidden="true" />
                  {tag}
                </button>
              ))}
            </div>
          </div>

          <div className="account-rating__section">
            <header>
              <h4>Share more details</h4>
              <p>Describe what worked well or what we should improve for your next visit.</p>
            </header>
            <textarea
              className="account-rating__textarea"
              rows={4}
              value={feedback}
              onChange={(event) => setFeedback(event.target.value)}
              placeholder="Share what you liked or what we can improve…"
            />
            <label htmlFor="account-rating-followup" className="account-rating__toggle">
              <input
                id="account-rating-followup"
                type="checkbox"
                checked={allowFollowUp}
                onChange={(event) => setAllowFollowUp(event.target.checked)}
              />
              <span>Allow the LaborConnect digital team to follow up via your registered email.</span>
            </label>
          </div>

          {submittedFeedback ? (
            <div className="account-rating__thanks">
              <h3>Thank you for the feedback!</h3>
              <p>We recorded a {rating}-star experience.</p>
              {selectedAspects.length > 0 ? (
                <p>
                  Focus areas noted: <strong>{selectedAspects.join(', ')}</strong>
                </p>
              ) : null}
              {trimmedFeedback ? <p>“{trimmedFeedback}”</p> : null}
              <p>
                {allowFollowUp
                  ? 'Our digital team may reach out through your registered email for clarifications.'
                  : 'We continuously iterate on LaborConnect based on member stories like yours.'}
              </p>
            </div>
          ) : (
            <div className="account-rating__actions">
              <button
                type="button"
                className="button button--secondary"
                onClick={handleSubmitRating}
                disabled={rating === 0}
              >
                Submit response
              </button>
              <button
                type="button"
                className="button button--ghost"
                onClick={() => {
                  setShowRate(false);
                  resetRatingState();
                }}
              >
                Cancel
              </button>
            </div>
          )}
        </AccountModal>
      )}

      {showVerificationPrompt && (
        <AccountModal
          title="Verification Required"
          tone="blue"
          size="sm"
          icon={ShieldOff}
          onClose={() => setShowVerificationPrompt(false)}
        >
          <div style={{ textAlign: 'center', padding: '8px 0 16px' }}>
            <p style={{ margin: '0 0 16px', color: '#334155', lineHeight: 1.5 }}>
              Please complete your verification to update this information.
            </p>
            <button
              type="button"
              onClick={() => setShowVerificationPrompt(false)}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px',
                padding: '10px 28px',
                background: 'linear-gradient(135deg, #2563eb, #1d4ed8)',
                color: '#ffffff',
                fontWeight: '600',
                fontSize: '0.92rem',
                border: 'none',
                borderRadius: '12px',
                cursor: 'pointer',
                boxShadow: '0 4px 14px rgba(37, 99, 235, 0.35)',
                transition: 'opacity 0.15s',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.opacity = '0.88'; }}
              onMouseLeave={(e) => { e.currentTarget.style.opacity = '1'; }}
            >
              <ShieldCheck size={15} />
              Got it, I understand
            </button>
          </div>
        </AccountModal>
      )}

      {false && (
        <>

      {showFaq && (
        <AccountModal
          title="Frequently Asked Questions"
          tone="blue"
          size="lg"
          icon={HelpCircle}
          onClose={() => setShowFaq(false)}
        >
          <div className="account-modal__list account-modal__list--faq">
            {faqEntries.map((entry) => (
              <div key={entry.question}>
                <span>{entry.question}</span>
                <strong>{entry.answer}</strong>
              </div>
            ))}
          </div>
        </AccountModal>
      )}

      {showAbout && (
        <AccountModal
          title="About LaborConnect"
          tone="purple"
          size="lg"
          icon={Info}
          onClose={() => setShowAbout(false)}
        >
          <div className="account-modal__list account-modal__list--about">
            {aboutDetails.map((entry) => (
              <div key={entry.label}>
                <span>{entry.label}</span>
                {entry.mapUrl ? (
                  <strong>
                    <a
                      className="account-modal__address-link"
                      href={entry.mapUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {entry.value}
                      <ExternalLink size={14} />
                    </a>
                  </strong>
                ) : (
                  <strong>{entry.value}</strong>
                )}
              </div>
            ))}
          </div>
          <div className="account-modal__list account-modal__list--accent account-modal__list--about">
            {supportContacts.map(({ icon: Icon, label, value }) => (
              <div key={label}>
                <span>{label}</span>
                <strong>
                  <Icon size={14} style={{ marginRight: 8 }} />
                  {value}
                </strong>
              </div>
            ))}
          </div>
          <p className="account-modal__footnote">
            LaborConnect is a proud founding affiliate of the Trade Union Congress of the Philippines, representing workers in
            19,000+ workplaces across Luzon.
          </p>
        </AccountModal>
      )}

      {showSupport && (
        <AccountModal
          title="Contact Member Support"
          tone="green"
          size="lg"
          icon={Phone}
          onClose={() => setShowSupport(false)}
        >
          <div className="account-modal__list account-modal__list--accent">
            {supportContacts.map(({ icon: Icon, label, value }) => (
              <div key={label}>
                <span>{label}</span>
                <strong>
                  <Icon size={14} style={{ marginRight: 8 }} />
                  {value}
                </strong>
              </div>
            ))}
          </div>

          <div className="account-modal__list">
            <div>
              <span>Emergency Assistance</span>
              <strong>Dial hotline and press 1 for urgent medical or legal support. Available 24/7.</strong>
            </div>
            <div>
              <span>Member Services Desk</span>
              <strong>Email documents to benefits@laborconnect.ph for faster processing of claims and requests.</strong>
            </div>
            <div>
              <span>Regional Offices</span>
              <strong>
                Luzon HQ, ALU-AFILUTE Building, 262 15th Ave., Cubao, Quezon City
              </strong>
            </div>
          </div>

          <p className="account-modal__footnote">
            Tip: Attach your membership ID or digital ID screenshot when emailing to speed up verification.
          </p>
        </AccountModal>
      )}

      {showSettings && (
        <AccountModal
          title="Account Settings"
          tone="blue"
          size="lg"
          icon={Settings}
          onClose={() => setShowSettings(false)}
        >
          <div className="account-settings">
            <div className="account-settings__group">
              {preferenceOptions.map(({ key, label, description }) => (
                <label key={key} className="account-toggle">
                  <input
                    type="checkbox"
                    checked={preferences[key]}
                    onChange={() => togglePreference(key)}
                    aria-label={label}
                  />
                  <span className="account-toggle__switch">
                    <span className="account-toggle__thumb" />
                  </span>
                  <div className="account-toggle__meta">
                    <strong>{label}</strong>
                    <p>{description}</p>
                  </div>
                </label>
              ))}
            </div>

            <div className="account-settings__cta">
              <div className="account-settings__cta-text">
                <strong>Keep your account secure</strong>
                <span>Refresh your password regularly to protect your membership information.</span>
              </div>
              <button
                type="button"
                className="button button--secondary"
                onClick={() => {
                  setShowSettings(false);
                  setShowChangePassword(true);
                }}
              >
                Change password
              </button>
            </div>
          </div>

          <div className="account-modal__list account-modal__list--accent">
            <div>
              <span>Last password update</span>
              <strong>{formatDate(resolvedPasswordUpdatedAt, { month: 'long', day: 'numeric', year: 'numeric' })}</strong>
            </div>
            <div>
              <span>Session devices</span>
              <strong>{sessionDeviceLabel || 'Session data unavailable'}</strong>
            </div>
          </div>

          <p className="account-modal__footnote">
            Preference changes are stored locally until backend integration is enabled. Contact support for security updates.
          </p>
        </AccountModal>
      )}

      {showChangePassword && (
        <AccountModal
          title="Change Password"
          tone="purple"
          size="md"
          icon={Shield}
          onClose={() => {
            setShowChangePassword(false);
            resetPasswordState();
          }}
        >
          <form className="account-modal__form" onSubmit={handleChangePasswordSubmit}>
            <div
              className={`account-modal__field${currentPasswordReady ? ' account-modal__field--valid' : ''}`}
            >
              <label htmlFor="account-password-current">Current password</label>
              <input
                id="account-password-current"
                type="password"
                value={passwordForm.current}
                onChange={(event) => updatePasswordForm('current', event.target.value)}
                placeholder="Enter your current password"
                autoComplete="current-password"
              />
            </div>
            <div
              className={`account-modal__field${newPasswordIsValid ? ' account-modal__field--valid' : ''}`}
            >
              <label htmlFor="account-password-new">New password</label>
              <div className="account-modal__input-group">
                <input
                  id="account-password-new"
                  type={showNewPassword ? 'text' : 'password'}
                  value={passwordForm.next}
                  onChange={(event) => updatePasswordForm('next', event.target.value)}
                  placeholder="Choose a new password"
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  className="account-modal__input-toggle"
                  onClick={() => setShowNewPassword((prev) => !prev)}
                  aria-label={showNewPassword ? 'Hide new password' : 'Show new password'}
                >
                  {showNewPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            <div className="account-password-reqs">
              <strong>Password Requirements:</strong>
              <ul>
                {passwordRequirements.map(({ label, test }) => {
                  const met = test(trimmedNewPassword);
                  return (
                    <li
                      key={label}
                      className={`account-password-req${met ? ' account-password-req--met' : ''}`}
                    >
                      <span className="account-password-req__icon" aria-hidden="true">
                        {met ? '✓' : '•'}
                      </span>
                      <span>{label}</span>
                    </li>
                  );
                })}
              </ul>
            </div>
            <div
              className={`account-modal__field${
                confirmPasswordMatches ? ' account-modal__field--valid' : ''
              }`}
            >
              <label htmlFor="account-password-confirm">Confirm new password</label>
              <div className="account-modal__input-group">
                <input
                  id="account-password-confirm"
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={passwordForm.confirm}
                  onChange={(event) => updatePasswordForm('confirm', event.target.value)}
                  placeholder="Re-enter the new password"
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  className="account-modal__input-toggle"
                  onClick={() => setShowConfirmPassword((prev) => !prev)}
                  aria-label={showConfirmPassword ? 'Hide confirmation password' : 'Show confirmation password'}
                >
                  {showConfirmPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            {passwordStatus.error ? (
              <p className="account-modal__status account-modal__status--error">{passwordStatus.error}</p>
            ) : null}
            {passwordStatus.success ? (
              <p className="account-modal__status account-modal__status--success">{passwordStatus.success}</p>
            ) : null}

            <div className="account-modal__actions">
              <button type="submit" className="button button--secondary">
                Update password
              </button>
              <button
                type="button"
                className="button button--ghost"
                onClick={() => {
                  setShowChangePassword(false);
                  resetPasswordState();
                }}
              >
                Cancel
              </button>
            </div>
          </form>
        </AccountModal>
      )}

      {showRate && (
        <AccountModal
          title="Rate our Website"
          tone="blue"
          size="lg"
          icon={Star}
          onClose={() => {
            setShowRate(false);
            resetRatingState();
          }}
        >
          <div className="account-rating__hero">
            <div className="account-rating__icon">⭐</div>
            <div className="account-rating__summary">
              <h3>Overall experience</h3>
              <p>Tell us how well the LaborConnect member portal supports your needs today.</p>
              <div
                className="account-rating__stars"
                role="radiogroup"
                aria-label="Overall website experience rating"
              >
                {[1, 2, 3, 4, 5].map((value) => (
                  <button
                    type="button"
                    key={value}
                    className={`account-rating__star${rating >= value ? ' account-rating__star--active' : ''}`}
                    onClick={() => setRating(value)}
                    aria-label={`Rate ${value} star${value > 1 ? 's' : ''}`}
                    aria-pressed={rating === value}
                  >
                    ★
                  </button>
                ))}
              </div>
              <p className="account-rating__hint">{ratingHint}</p>
            </div>
          </div>

          <div className="account-rating__section">
            <header>
              <h4>Focus areas</h4>
              <p>Select the parts of the experience you want us to improve or keep strong.</p>
            </header>
            <div
              className="account-rating__chips"
              role="group"
              aria-label="Focus areas for feedback"
            >
              {feedbackTags.map((tag) => (
                <button
                  type="button"
                  key={tag}
                  className={`account-rating__chip${selectedAspects.includes(tag) ? ' account-rating__chip--active' : ''}`}
                  onClick={() => toggleAspect(tag)}
                >
                  <span className="account-rating__chip-dot" aria-hidden="true" />
                  {tag}
                </button>
              ))}
            </div>
          </div>

          <div className="account-rating__section">
            <header>
              <h4>Share more details</h4>
              <p>Describe what worked well or what we should improve for your next visit.</p>
            </header>
            <textarea
              className="account-rating__textarea"
              rows={4}
              value={feedback}
              onChange={(event) => setFeedback(event.target.value)}
              placeholder="Share what you liked or what we can improve…"
            />
            <label htmlFor="account-rating-followup" className="account-rating__toggle">
              <input
                id="account-rating-followup"
                type="checkbox"
                checked={allowFollowUp}
                onChange={(event) => setAllowFollowUp(event.target.checked)}
              />
              <span>Allow the LaborConnect digital team to follow up via your registered email.</span>
            </label>
          </div>

          {submittedFeedback ? (
            <div className="account-rating__thanks">
              <h3>Thank you for the feedback!</h3>
              <p>We recorded a {rating}-star experience.</p>
              {selectedAspects.length > 0 ? (
                <p>
                  Focus areas noted: <strong>{selectedAspects.join(', ')}</strong>
                </p>
              ) : null}
              {trimmedFeedback ? <p>“{trimmedFeedback}”</p> : null}
              <p>
                {allowFollowUp
                  ? 'Our digital team may reach out through your registered email for clarifications.'
                  : 'We continuously iterate on LaborConnect based on member stories like yours.'}
              </p>
            </div>
          ) : (
            <div className="account-rating__actions">
              <button
                type="button"
                className="button button--secondary"
                onClick={handleSubmitRating}
                disabled={rating === 0}
              >
                Submit response
              </button>
              <button
                type="button"
                className="button button--ghost"
                onClick={() => {
                  setShowRate(false);
                  resetRatingState();
                }}
              >
                Cancel
              </button>
            </div>
          )}
        </AccountModal>
      )}
        </>
      )}
    </AppLayout>
  );
}

const PenIcon = PencilLine;

AccountPage.propTypes = {
  user: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    email: PropTypes.string,
    phone: PropTypes.string,
    address: PropTypes.string,
    company: PropTypes.string,
    position: PropTypes.string,
    department: PropTypes.string,
    unionPosition: PropTypes.string,
    unionAffiliation: PropTypes.string,
    membershipDate: PropTypes.string,
    verifiedDate: PropTypes.string,
    passwordUpdatedAt: PropTypes.string,
    currentSession: PropTypes.shape({
      browser: PropTypes.string,
      deviceType: PropTypes.string,
      location: PropTypes.string,
    }),
    profilePicture: PropTypes.string,
    digitalId: PropTypes.string,
    dateOfBirth: PropTypes.string,
    gender: PropTypes.string,
    otherGender: PropTypes.string,
    religion: PropTypes.string,
    education: PropTypes.string,
    yearsEmployed: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    emergencyContact: PropTypes.shape({
      name: PropTypes.string,
      relationship: PropTypes.string,
      phone: PropTypes.string,
    }),
  }).isRequired,
  onLogout: PropTypes.func.isRequired,
};
